import { useId, useState } from "react";
import type {
  ConnectionCredentialValue,
  ToolkitConnectionSetup,
} from "@/api/integrations";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { TextInput } from "@/components/ui/text-input";
import { SelectField } from "@/components/ui/select-field";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesConnectionPromptMessages } from "@/i18n/messages/frames-connection-prompt";
import { openExternalUrl } from "@/lib/open-external-url";

function defaults(setup: ToolkitConnectionSetup): Record<string, string> {
  return Object.fromEntries(
    setup.fields.map((field) => [
      field.name,
      !field.secret && field.default !== undefined ? String(field.default) : "",
    ]),
  );
}

export function ConnectionCredentialForm({
  setup,
  busy,
  onSubmit,
}: {
  setup: ToolkitConnectionSetup;
  busy: boolean;
  onSubmit: (fields: Record<string, ConnectionCredentialValue>) => void;
}) {
  const t = useT(framesConnectionPromptMessages);
  const tCommon = useT(commonMessages);
  const id = useId();
  // These values live only in this account-keyed component. Never persist them.
  const [values, setValues] = useState(() => defaults(setup));
  const [linkError, setLinkError] = useState(false);
  const valid = setup.fields.every((field) => {
    const value = values[field.name]?.trim() ?? "";
    if (!value) return !field.required;
    return field.type !== "number" || Number.isFinite(Number(value));
  });
  const setupUrl = setup.setup_url?.startsWith("https://")
    ? setup.setup_url
    : null;

  return (
    <form
      className="ph-no-capture flex w-full max-w-sm flex-col gap-3 text-left"
      data-testid="connection-credential-form"
      data-ph-no-capture="true"
      autoComplete="off"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || !valid) return;
        const fields: Record<string, ConnectionCredentialValue> = {};
        for (const field of setup.fields) {
          const value = values[field.name]?.trim() ?? "";
          if (!value) continue;
          fields[field.name] =
            field.type === "boolean"
              ? value === "true"
              : field.type === "number"
                ? Number(value)
                : value;
        }
        setValues(defaults(setup));
        onSubmit(fields);
      }}
    >
      <p className="text-canvas-sm text-secondary">
        {setup.instructions || t("apiKeyInstructions")}
      </p>
      {setupUrl && (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          data-testid="connection-api-key-page"
          onClick={() => {
            setLinkError(false);
            void openExternalUrl(setupUrl).catch(() => setLinkError(true));
          }}
        >
          {t("openApiKeys", { app: setup.display_name })}
        </Button>
      )}
      {linkError && (
        <p role="alert" className="text-canvas-sm text-danger">
          {t("openPageFailed")}
        </p>
      )}
      {setup.fields.map((field) => (
        <FormField
          key={field.name}
          label={
            field.required
              ? field.label
              : t("optionalField", { label: field.label })
          }
          htmlFor={`${id}-${field.name}`}
        >
          {field.type === "boolean" ? (
            <SelectField
              ariaLabel={field.label}
              value={values[field.name] ?? ""}
              options={[
                { value: "", label: t("chooseOption") },
                { value: "true", label: tCommon("yes") },
                { value: "false", label: tCommon("no") },
              ]}
              onChange={(value) =>
                setValues((current) => ({ ...current, [field.name]: value }))
              }
              disabled={busy}
              testId={`connection-credential-${field.name}`}
            />
          ) : (
            <TextInput
              id={`${id}-${field.name}`}
              name={field.name}
              type={
                field.secret
                  ? "password"
                  : field.type === "number"
                    ? "number"
                    : "text"
              }
              step={field.type === "number" ? "any" : undefined}
              value={values[field.name] ?? ""}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  [field.name]: event.target.value,
                }))
              }
              disabled={busy}
              required={field.required}
              autoComplete="off"
              spellCheck={false}
              data-testid={`connection-credential-${field.name}`}
              data-private="true"
            />
          )}
          {field.description && (
            <p className="text-canvas-sm text-muted">{field.description}</p>
          )}
        </FormField>
      ))}
      <Button
        type="submit"
        size="sm"
        disabled={!valid || busy}
        data-testid="connection-credentials-submit"
      >
        {busy ? t("connecting") : t("connect")}
      </Button>
    </form>
  );
}
